// Gallery of inline SVG icons for Special Hours types. This is the single source
// of truth for the available icons: the customer badge and the admin icon picker
// both read from SPECIAL_HOURS_ICONS. Adding an icon means adding a component here
// plus an entry in the map — nothing else changes.
//
// All icons share the style of the original badge icon: 24x24 viewBox, no fill,
// stroke = the type color, rounded caps/joins, decorative (aria-hidden).

const base = {
  fill: 'none',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

function Svg({ color, size = 15, children }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" stroke={color}
      style={{ flexShrink: 0 }} {...base}>
      {children}
    </svg>
  );
}

export function VideoCameraIcon({ color, size }) {
  return (
    <Svg color={color} size={size}>
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </Svg>
  );
}

export function CameraIcon({ color, size }) {
  return (
    <Svg color={color} size={size}>
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </Svg>
  );
}

export function StarIcon({ color, size }) {
  return (
    <Svg color={color} size={size}>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </Svg>
  );
}

export function HeartIcon({ color, size }) {
  return (
    <Svg color={color} size={size}>
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </Svg>
  );
}

export function PhoneIcon({ color, size }) {
  return (
    <Svg color={color} size={size}>
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
      <line x1="12" y1="18" x2="12.01" y2="18" />
    </Svg>
  );
}

export function MoonIcon({ color, size }) {
  return (
    <Svg color={color} size={size}>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </Svg>
  );
}

export function LeafIcon({ color, size }) {
  return (
    <Svg color={color} size={size}>
      <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.52-4.48 10-10 10z" />
      <path d="M2 21c0-3 1.85-5.36 5.08-6" />
    </Svg>
  );
}

export function DropletIcon({ color, size }) {
  return (
    <Svg color={color} size={size}>
      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
    </Svg>
  );
}

export function UsersIcon({ color, size }) {
  return (
    <Svg color={color} size={size}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </Svg>
  );
}

export function MusicIcon({ color, size }) {
  return (
    <Svg color={color} size={size}>
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </Svg>
  );
}

export function SparklesIcon({ color, size }) {
  return (
    <Svg color={color} size={size}>
      <path d="M12 3l1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3z" />
    </Svg>
  );
}

export function SunIcon({ color, size }) {
  return (
    <Svg color={color} size={size}>
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </Svg>
  );
}

export function ClockIcon({ color, size }) {
  return (
    <Svg color={color} size={size}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </Svg>
  );
}

// Map: icon key (stored in config.specialHourTypes[].icon) -> component.
export const SPECIAL_HOURS_ICONS = {
  video: VideoCameraIcon,
  camera: CameraIcon,
  star: StarIcon,
  heart: HeartIcon,
  phone: PhoneIcon,
  moon: MoonIcon,
  leaf: LeafIcon,
  droplet: DropletIcon,
  users: UsersIcon,
  music: MusicIcon,
  sparkles: SparklesIcon,
  sun: SunIcon,
  clock: ClockIcon,
};

export const SPECIAL_HOURS_ICON_LIST = Object.keys(SPECIAL_HOURS_ICONS);
export const DEFAULT_ICON = 'video';

// Fixed color palette — only values already present in the app's :root tokens.
// No new colors are introduced.
export const SPECIAL_HOURS_COLORS = [
  '#C8A96E', // gold (current badge)
  '#c17f3e', // --accent (amber)
  '#2d6a4f', // --green
  '#c0392b', // --red
  '#3b82f6', // blue (already used in lr-pill.blue)
  '#5c4a35', // --umber
];
